import type {
  TPayoutsByPackageAnalysisRes,
  TPayoutsByMonthRes,
} from '@src/types/reports';

import { ExportIcon } from '@features/ui/icons';
import { Button, Card } from '@features/ui';
import {
  PayoutsAmountReportTable,
  PayoutsTotalsReportTable,
} from '@features/admin/reports';
import { useState, useEffect } from 'react';
import { getPayoutsTotalsReport } from '@src/services';

export const PayoutsTotalsReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payoutsByPackage, setPayoutsByPackage] = useState<
    TPayoutsByPackageAnalysisRes[]
  >([]);
  const [payoutsByMonth, setPayoutsByMonth] = useState<TPayoutsByMonthRes[]>(
    []
  );

  // Fetch report data on component mount
  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getPayoutsTotalsReport();

      setPayoutsByPackage(response.payouts_by_package);
      setPayoutsByMonth(response.payouts_by_month);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load report data'
      );
      console.error('Error fetching payouts totals report:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div
        className={
          'flex justify-between lg:items-center gap-7.5 lg:flex-row flex-col'
        }
      >
        <div className={'text-xs flex lg:flex-row flex-col gap-x-8 gap-y-3'}>
          <h1 className={'text-[23px]'}>{`Payouts - Total Reports`}</h1>
          <p className={'max-w-[419px]'}>Payouts by PA Account - All Time</p>
        </div>
        <Button
          className={'h-9.5 border-1 border-[#C6C6C6] rounded-[3px] w-fit'}
          variant={'outline'}
        >
          <p>Export</p>
          <ExportIcon />
        </Button>
      </div>
      <Card className={'mt-4.5'}>
        <div
          className={
            'flex lg:flex-row flex-col justify-between items-start mb-5 gap-6'
          }
        >
          <div className={'flex gap-6 flex-wrap'} />
          <div className={'flex gap-5'}>
            <Button
              disabled={isLoading}
              loading={isLoading}
              size={'sm'}
              variant={'lime'}
              onClick={fetchReportData}
            >
              Get
            </Button>
          </div>
        </div>

        {error && (
          <div
            className={
              'bg-red-50 border border-red-200 text-red-600 p-3 rounded mb-5'
            }
          >
            {error}
          </div>
        )}

        <div className={'flex flex-col gap-15'}>
          {isLoading ? (
            <div className={'text-center py-8'}>Loading report data...</div>
          ) : (
            <>
              <PayoutsTotalsReportTable data={payoutsByPackage} />
              <PayoutsAmountReportTable data={payoutsByMonth} />
            </>
          )}
        </div>
      </Card>
    </div>
  );
};
